import { useEffect, useRef, useState } from 'react';
import type { Category, Product, ProductInput } from '@/services/products';
import SafeImage from '@/components/base/SafeImage';
import { saveProduct, uploadProductImage, deleteProductImage, syncProductStock, normalizeImageUrl } from '@/services/products';
import { fetchInventory, type SizeItem } from '@/services/inventory';

interface ProductModalProps {
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = (categoryId: string): ProductInput => ({
  id: undefined,
  name: '',
  sku: '',
  categoryId,
  description: '',
  features: [],
  materials: '',
  story: '',
  benefits: [],
  commercialPrice: 0,
  unitCost: 0,
  images: [],
  videos: [],
  active: true,
  lineType: 'premium',
  chargeShipping: false,
});

function listToText(list: string[]): string {
  return (list ?? []).join('\n');
}

function textToList(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

export default function ProductModal({ product, categories, onClose, onSaved }: ProductModalProps) {
  const isEditing = !!product;
  const firstCatId = categories[0]?.id ?? '';

  const [form, setForm] = useState<ProductInput>(() =>
    product
      ? {
          id: product.id,
          name: product.name,
          sku: product.sku,
          categoryId: product.categoryId,
          description: product.description,
          features: product.features,
          materials: product.materials,
          story: product.story,
          benefits: product.benefits,
          commercialPrice: product.commercialPrice,
          unitCost: product.unitCost,
          images: product.images,
          videos: product.videos,
          active: product.active,
          lineType: product.lineType,
          chargeShipping: product.chargeShipping,
        }
      : emptyForm(firstCatId),
  );

  // Sincroniza la categoría cuando llegan las categorías reales (para evitar
  // que el select quede vacío si el modal se abrió antes de que carguen).
  useEffect(() => {
    if (!isEditing && categories.length > 0) {
      setForm((f) => {
        if (!f.categoryId) return { ...f, categoryId: categories[0].id };
        const exists = categories.some((c) => c.id === f.categoryId);
        if (!exists) return { ...f, categoryId: categories[0].id };
        return f;
      });
    }
  }, [categories, isEditing]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalImagesRef = useRef<string[]>(product?.images ?? []);
  const uploadedRef = useRef<string[]>([]);
  const savedRef = useRef(false);

  const [sizes, setSizes] = useState<SizeItem[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  // Carga las tallas activas y, en edición, el stock actual por talla.
  useEffect(() => {
    let cancelled = false;
    fetchInventory()
      .then((res) => {
        if (cancelled) return;
        const activeSizes = res.sizes.filter((s) => s.active);
        setSizes(activeSizes);
        if (product) {
          const invs = res.inventory.filter((i) => i.productId === product.id);
          setSelected(invs.map((i) => i.sizeId));
        } else {
          setSelected([]);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [product]);

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);

    const urls: string[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" no es una imagen válida (solo JPG/PNG/WebP).`);
        continue;
      }
      const { url, error: upErr } = await uploadProductImage(file);
      if (upErr) {
        setError(`Error al subir "${file.name}": ${upErr}`);
        continue;
      }
      if (url) urls.push(url);
    }

    if (urls.length > 0) {
      uploadedRef.current.push(...urls);
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // Quita la imagen solo de la vista local. El borrado real en Storage se
  // ejecuta al guardar, para que "cancelar" no deje imágenes huérfanas ni rotas.
  function removeImage(index: number) {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  }

  // Cierra el modal. Si no se guardó, limpia los archivos subidos en esta
  // sesión para no dejar imágenes huérfanas en Storage.
  async function handleClose() {
    if (!savedRef.current) {
      for (const url of uploadedRef.current) {
        await deleteProductImage(url);
      }
    }
    onClose();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function set<K extends keyof ProductInput>(key: K, value: ProductInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    if (!form.name.trim()) {
      setError('El nombre es obligatorio.');
      setSaving(false);
      return;
    }
    if (!form.sku.trim()) {
      setError('El SKU es obligatorio.');
      setSaving(false);
      return;
    }
    if (!form.categoryId) {
      setError('Debes seleccionar una categoría.');
      setSaving(false);
      return;
    }

    const { data: saved, error: saveError } = await saveProduct(form, categories);
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }

    // Sincroniza variantes y stock por talla: solo las tallas marcadas.
    if (saved?.id) {
      const entries = sizes
        .filter((s) => selected.includes(s.id))
        .map((s) => ({
          sizeId: s.id,
          quantity: 0,
        }));
      const { error: stockError } = await syncProductStock(saved.id, form.sku.trim(), entries);
      if (stockError) console.warn('[products] stock sync failed:', stockError);
    }

    // Borra en Storage solo lo que ya no se conserva en el producto guardado:
    // imágenes originales quitadas y archivos subidos en esta sesión que se
    // quitaron antes de guardar. Así "cancelar" no deja huérfanas.
    const finalImages = form.images;
    const toDelete = new Set<string>();
    for (const url of originalImagesRef.current) {
      if (!finalImages.includes(url)) toDelete.add(url);
    }
    for (const url of uploadedRef.current) {
      if (!finalImages.includes(url)) toDelete.add(url);
    }
    for (const url of toDelete) {
      await deleteProductImage(url);
    }

    savedRef.current = true;
    setSuccess(true);
    onSaved();
    setTimeout(() => {
      onClose();
    }, 900);
  }

  const inputCls =
    'w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-accent-300';
  const labelCls = 'block text-xs font-medium text-foreground-600 mb-1.5';

  // El producto solo despliega las tallas de su propia línea: premium -> tallas
  // premium; sencilla -> tallas básicas.
  const expectedSizeType = form.lineType === 'sencilla' ? 'basica' : 'premium';
  const availableSizes = sizes.filter((s) => s.sizeType === expectedSizeType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      ></div>

      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-xl border border-background-200 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
          <div>
            <h2 className="text-base font-semibold text-foreground-950">
              {isEditing ? 'Editar producto' : 'Nuevo producto'}
            </h2>
            <p className="text-xs text-foreground-500 mt-0.5">
              {isEditing
                ? `Modificando: ${product?.name}`
                : 'Completa los datos para crear una referencia nueva en el catálogo.'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-5 space-y-5">
          {success && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5">
              <i className="ri-check-line"></i>
              {isEditing ? 'Producto actualizado correctamente.' : 'Producto creado correctamente.'}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Identidad */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-900 uppercase tracking-wide mb-3">
              Identidad
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nombre *</label>
                <input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Ej. Pastor Alemán"
                />
              </div>
              <div>
                <label className={labelCls}>SKU *</label>
                <input
                  className={inputCls}
                  value={form.sku}
                  onChange={(e) => set('sku', e.target.value)}
                  placeholder="Ej. PCP-PA-001"
                />
              </div>
            </div>
          </div>

          {/* Clasificación */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-900 uppercase tracking-wide mb-3">
              Clasificación
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Categoría *</label>
                {categories.length === 0 ? (
                  <p className="text-sm text-red-600 flex items-center gap-1.5">
                    <i className="ri-error-warning-line"></i>
                    No hay categorías disponibles. Carga o recarga la página.
                  </p>
                ) : (
                  <select
                    className={inputCls}
                    value={form.categoryId}
                    onChange={(e) => set('categoryId', e.target.value)}
                  >
                    <option value="" disabled>Selecciona una categoría...</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className={labelCls}>Estado</label>
                <label className="flex items-center gap-2.5 h-[38px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => set('active', e.target.checked)}
                    className="w-4 h-4 accent-accent-500 cursor-pointer"
                  />
                  <span className="text-sm text-foreground-700">
                    {form.active ? 'Activo (visible en tienda)' : 'Inactivo (oculto)'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Línea comercial y envío */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-900 uppercase tracking-wide mb-3">
              Línea comercial y envío
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Línea del producto</label>
                <select
                  className={inputCls}
                  value={form.lineType}
                  onChange={(e) => {
                    const lineType = e.target.value;
                    const wantType = lineType === 'sencilla' ? 'basica' : 'premium';
                    setForm((f) => ({
                      ...f,
                      lineType,
                      chargeShipping: lineType === 'sencilla',
                    }));
                    // Quita las tallas seleccionadas que ya no corresponden a la línea.
                    setSelected((prev) =>
                      prev.filter((id) => sizes.find((x) => x.id === id)?.sizeType === wantType),
                    );
                  }}
                >
                  <option value="premium">Premium (envío incluido en el precio)</option>
                  <option value="sencilla">Sencilla / Pantuflas (cobra flete)</option>
                </select>
                <p className="text-xs text-foreground-400 mt-1">
                  La línea premium incluye el flete; la sencilla suma el envío de Interrapidísimo en el checkout y la venta directa.
                </p>
              </div>
              <div>
                <label className={labelCls}>Cobrar envío a este producto</label>
                <label className="flex items-center gap-2.5 h-[38px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.chargeShipping}
                    onChange={(e) => set('chargeShipping', e.target.checked)}
                    className="w-4 h-4 accent-accent-500 cursor-pointer"
                  />
                  <span className="text-sm text-foreground-700">
                    {form.chargeShipping ? 'Sí, cobra flete por ciudad' : 'No, envío gratis'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Tallas disponibles */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-900 uppercase tracking-wide mb-3">
              Tallas disponibles
            </h3>
            <p className="text-xs text-foreground-500 mb-3">
              Marca las tallas en las que se vende este producto. Solo se muestran las tallas de tipo{' '}
              <strong>{expectedSizeType === 'basica' ? 'Básica' : 'Premium'}</strong> según la línea. El stock inicia en 0 y se carga después desde Compras.
            </p>
            {availableSizes.length === 0 ? (
              <p className="text-sm text-foreground-500 flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Cargando tallas...
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {availableSizes.map((s) => {
                  const isSelected = selected.includes(s.id);
                  return (
                    <div
                      key={s.id}
                      className={`border rounded-md px-3 py-2.5 transition-colors ${
                        isSelected ? 'border-accent-300 bg-accent-50' : 'border-background-200'
                      }`}
                    >
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelected((prev) => [...prev, s.id]);
                            } else {
                              setSelected((prev) => prev.filter((id) => id !== s.id));
                            }
                          }}
                          className="w-4 h-4 accent-accent-500 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-foreground-800">{s.name}</span>
                        {s.sizeRange && (
                          <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                            {s.sizeRange}
                          </span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Contenido */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-900 uppercase tracking-wide mb-3">
              Contenido
            </h3>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Descripción</label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={3}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Describe el producto para el cliente..."
                />
              </div>
              <div>
                <label className={labelCls}>Materiales (separados por coma)</label>
                <input
                  className={inputCls}
                  value={form.materials}
                  onChange={(e) => set('materials', e.target.value)}
                  placeholder="Microfibra premium, suela EVA, memory foam"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Características (una por línea)</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={4}
                    value={listToText(form.features)}
                    onChange={(e) => set('features', textToList(e.target.value))}
                    placeholder={'Material antideslizante\nPlantilla memory foam'}
                  />
                </div>
                <div>
                  <label className={labelCls}>Beneficios (uno por línea)</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={4}
                    value={listToText(form.benefits)}
                    onChange={(e) => set('benefits', textToList(e.target.value))}
                    placeholder={'Comodidad todo el día\nFáciles de limpiar'}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Historia</label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={3}
                  value={form.story}
                  onChange={(e) => set('story', e.target.value)}
                  placeholder="La historia detrás del diseño..."
                />
              </div>
            </div>
          </div>

          {/* Medios */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-900 uppercase tracking-wide mb-3">
              Medios
            </h3>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Imágenes</label>
                {form.images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
                    {form.images.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative rounded-lg overflow-hidden border border-background-200 aspect-square bg-background-50"
                      >
                        <SafeImage src={img} alt={`Imagen ${idx + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors cursor-pointer"
                          aria-label={`Quitar imagen ${idx + 1}`}
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFiles}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 text-sm px-4 py-2.5 border border-dashed border-background-300 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>Subiendo...
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-cloud-2-line"></i>Subir imagen
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-400 mt-1.5">
                  Formatos JPG, PNG o WebP. También puedes pegar URLs en el campo de abajo.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Añadir URL de imagen (una por línea)</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={3}
                    value={listToText(form.images)}
                    onChange={(e) => set('images', textToList(e.target.value).map(normalizeImageUrl))}
                    placeholder="https://..."
                  />
                  <p className="text-xs text-foreground-400 mt-1">
                    URLs directas (.jpg/.png/.webp) o enlaces de Google Drive (se convierten solos). Google Photos e Instagram no permiten incrustar sus fotos; para esas usa «Subir imagen».
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Videos (una URL por línea)</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={3}
                    value={listToText(form.videos)}
                    onChange={(e) => set('videos', textToList(e.target.value))}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-100">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || categories.length === 0}
            className="px-5 py-2 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-1.5"></i>
                {isEditing ? 'Guardar cambios' : 'Crear producto'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}